import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Alert, Button, Checkbox, FieldError, Input, InputGroup, Label, ListBox, Modal, Select, TextField } from '@heroui/react'
import { api, ApiError, type SignInChallenge } from '../lib/api'
import { backToAddress, endsCodeStep, stepAsks } from '../lib/signInCode'
import { explain } from '../lib/staffErrors'
import { clearSignedOut, signedOut, tryWindowsSignIn, windowsStart } from '../lib/negotiate'
import { ssoRefusal, ssoStart, withoutRefusal } from '../lib/sso'
import type { Identity, OrganisationChoice, SSOChoice } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { EntryFrame } from './EntryFrame'

/**
 * Signing in.
 *
 * The organisation field appears only where it is needed. A single-organisation
 * installation — which is nearly every on-premise one — never shows it, because a
 * field an operator has to fill in the same way every morning is a field they will
 * eventually get wrong; its name is written above the form instead, so the person
 * still sees whose screen this is. Where a server hosts several organisations, the
 * list is fetched before anybody types, so the first attempt works rather than the
 * second.
 */
/**
 * A whole branch signs in at nine o'clock, and the server checks only so many
 * passwords at once (503 SIGN_IN_BUSY, Retry-After). Asked again a moment later,
 * with a little spread so that a room of desks does not retry in step, rather
 * than shown to the person as an error they would retry by hand anyway.
 */
async function signInPatiently<T>(attempt: () => Promise<T>): Promise<T> {
  for (let tries = 1; ; tries++) {
    try {
      return await attempt()
    } catch (err) {
      if (!(err instanceof ApiError) || err.code !== 'SIGN_IN_BUSY' || tries >= 6) throw err
      await new Promise((resolve) => setTimeout(resolve, 700 + Math.random() * 1600))
    }
  }
}

export function SignIn({
  onSignedIn,
  notice,
}: {
  onSignedIn: (identity: Identity) => void
  notice?: string
}) {
  const { lang, say } = useStaffText()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  // Off: the session cookie ends when the browser closes (a shared branch PC).
  const [remember, setRemember] = useState(true)
  const [forgot, setForgot] = useState(false)
  const [organisations, setOrganisations] = useState<OrganisationChoice[]>([])
  const [organisation, setOrganisation] = useState('')
  // Kept as the raw error rather than a resolved sentence, and resolved through
  // explain() at render (below): the language menu in the corner can change
  // lang after a failed attempt is already showing, and a string baked in at
  // catch-time would be left in whatever language was current then.
  // A single sign-on round trip that came back refused says why here.
  const [error, setError] = useState<unknown>(() => ssoRefusal(window.location.search))
  const [busy, setBusy] = useState(false)
  const [providers, setProviders] = useState<SSOChoice[]>([])
  // Windows sign-in's route for this organisation, '' when it is off.
  const [windows, setWindows] = useState('')
  // Whether the page asks for a ticket unprompted (Try automatically).
  const [windowsAuto, setWindowsAuto] = useState(false)
  // A refusal sent back here (?sso=) is shown, not tried again quietly.
  const [cameBack] = useState(() => new URLSearchParams(window.location.search).has('sso'))
  const triedWindows = useRef(false)
  // A master account's second step: the challenge its emailed code answers.
  const [challenge, setChallenge] = useState<SignInChallenge | null>(null)
  const [code, setCode] = useState('')
  const [email, setEmail] = useState('')
  // The code step came from an address given here, so Back can return to it.
  const [fromAddress, setFromAddress] = useState(false)
  const asks = challenge ? stepAsks(challenge) : null

  useEffect(() => {
    let live = true
    api
      .organisations()
      .then((r) => {
        if (!live) return
        setOrganisations(r.organisations)
        const only = r.organisations.length === 1 ? r.organisations[0] : undefined
        if (only) setOrganisation(only.slug)
      })
      // A failure here is not fatal: a single-organisation server does not need
      // the list at all, and the sign-in below will say so if it does.
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  const mustChoose = organisations.length > 1

  useEffect(() => {
    if (window.location.search.includes('sso=')) window.history.replaceState(null, '', withoutRefusal(window.location.href))
  }, [])

  // The organisation's sign-in service: the host's, the only one, or the one chosen.
  const ssoFor = mustChoose ? organisation : ''
  useEffect(() => {
    let live = true
    if (ssoFor === '__installation__') {
      setProviders([])
      return
    }
    api
      .ssoChoices(ssoFor || undefined)
      .then((r) => live && setProviders(r.providers))
      .catch(() => live && setProviders([]))
    return () => {
      live = false
    }
  }, [ssoFor])

  useEffect(() => {
    let live = true
    if (ssoFor === '__installation__') {
      setWindows('')
      setWindowsAuto(false)
      return
    }
    api
      .negotiateChoice(ssoFor || undefined)
      .then((r) => {
        if (!live) return
        setWindows(r.start)
        setWindowsAuto(r.auto === true)
      })
      .catch(() => {
        if (!live) return
        setWindows('')
        setWindowsAuto(false)
      })
    return () => {
      live = false
    }
  }, [ssoFor])

  // Once per page, quietly, when Try automatically is on: no ticket answers 401 and the form stays.
  useEffect(() => {
    if (!windows || !windowsAuto || triedWindows.current || cameBack || signedOut()) return
    triedWindows.current = true
    setBusy(true)
    void tryWindowsSignIn(windows)
      .then((answer) => {
        if (!answer) return
        if (answer.home && new URL(answer.home).host !== window.location.host) {
          window.location.assign(answer.home)
          return
        }
        onSignedIn(answer)
      })
      .finally(() => setBusy(false))
  }, [windows, windowsAuto, cameBack, onSignedIn])

  // The installation's owner belongs to no organisation, so on a server with
  // several there has to be something for them to pick that is not one of them.
  // A sentinel rather than an empty value, because an empty value is also what
  // the placeholder has, and "did not choose" and "chose the installation" must
  // not be the same answer on a form that requires a choice.
  const INSTALLATION = '__installation__'

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const named = mustChoose && organisation !== INSTALLATION ? organisation : undefined
      const answer = !challenge
        ? await signInPatiently(() => api.login(username.trim(), password, named, remember))
        : asks === 'email'
          ? await api.loginEmail(challenge.challenge, email.trim())
          : await api.loginCode(challenge.challenge, code.trim(), remember)
      if ('challenge' in answer) {
        setFromAddress(asks === 'email')
        setChallenge(answer)
        setCode('')
        return
      }
      clearSignedOut()
      // Signed in at the central address: the organisation works at its own.
      if (answer.home && new URL(answer.home).host !== window.location.host) {
        window.location.assign(answer.home)
        return
      }
      onSignedIn(answer)
    } catch (err) {
      // The server's own sentence is shown. An operator who is locked out needs to
      // know whether to wait or to fetch a supervisor, and "Sign-in failed" alone
      // tells them neither.
      setError(err)
      // An ended or locked code step goes back to the password: the next code
      // comes only from signing in again.
      if (challenge && endsCodeStep(err)) {
        setChallenge(null)
        setCode('')
        setFromAddress(false)
      }
    } finally {
      setBusy(false)
    }
  }

  // Resolved here, not in the catch above: the reader can switch language after
  // a failed attempt is already on screen, and this reruns on every render so
  // the sentence never goes stale.
  const errorText = error === null ? '' : explain(error, lang, 'common.unreachable')
  // A single-organisation server names itself, so the person sees whose screen this is.
  const sub = organisations.length === 1 ? organisations[0]!.name : say('entry.toContinue')

  return (
    <EntryFrame>
      <div className="entry-head">
        <h1 className="entry-title">{say('entry.system')}</h1>
        <p className="entry-lead">{sub}</p>
      </div>
      <form className="entry-form" onSubmit={submit}>
        {notice && !errorText && (
          <Alert status="warning" role="status">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Description>{notice}</Alert.Description>
            </Alert.Content>
          </Alert>
        )}
        {errorText && (
          <Alert status="danger" role="alert">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Description>{errorText}</Alert.Description>
            </Alert.Content>
          </Alert>
        )}

        {mustChoose && (
          <Select
            className="entry-field"
            name="organisation"
            value={organisation === '' ? null : organisation}
            onChange={(next) => next != null && setOrganisation(String(next))}
            placeholder={say('common.choose')}
            isRequired
            fullWidth
          >
            <Label>{say('common.organisation')}</Label>
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {[
                  ...organisations.map((o) => ({ value: o.slug, label: o.name })),
                  { value: INSTALLATION, label: say('entry.thisInstallation') },
                ].map((o) => (
                  <ListBox.Item key={o.value} id={o.value} textValue={o.label}>
                    {o.label}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
        )}

        <TextField className="entry-field" name="username" value={username} onChange={setUsername} isRequired fullWidth>
          <Label>{say('entry.username')}</Label>
          <InputGroup fullWidth className="entry-input">
            <InputGroup.Prefix>
              <Icon name="user" size={18} />
            </InputGroup.Prefix>
            <InputGroup.Input autoComplete="username" autoCapitalize="none" spellCheck={false} autoFocus />
          </InputGroup>
          <FieldError />
        </TextField>

        <TextField
          className="entry-field"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={setPassword}
          isRequired
          fullWidth
        >
          <Label>{say('entry.password')}</Label>
          <InputGroup fullWidth className="entry-input">
            <InputGroup.Prefix>
              <Icon name="lock" size={18} />
            </InputGroup.Prefix>
            <InputGroup.Input autoComplete="current-password" />
            <InputGroup.Suffix>
              <Button
                isIconOnly
                variant="ghost"
                size="sm"
                className="entry-eye"
                onPress={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? say('entry.hidePassword') : say('entry.showPassword')}
                aria-pressed={showPassword}
              >
                <Icon name={showPassword ? 'eyeOff' : 'eye'} size={18} />
              </Button>
            </InputGroup.Suffix>
          </InputGroup>
          <FieldError />
        </TextField>

        {!challenge && (
          <div className="entry-row">
            <Checkbox isSelected={remember} onChange={setRemember}>
              <Checkbox.Content>
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                {say('entry.keepSignedIn')}
              </Checkbox.Content>
            </Checkbox>
            <Button variant="ghost" size="sm" className="entry-link" onPress={() => setForgot(true)}>
              {say('entry.forgot')}
            </Button>
          </div>
        )}

        {asks === 'email' && (
          <TextField className="entry-field" name="email" type="email" value={email} onChange={setEmail} isRequired fullWidth>
            <Label>{say('entry.yourEmail')}</Label>
            <Input autoComplete="email" autoFocus />
            <FieldError />
          </TextField>
        )}

        {challenge && asks === 'code' && (
          <TextField className="entry-field" name="code" value={code} onChange={setCode} isRequired fullWidth>
            <Label>{say('entry.codeSent', { to: challenge.sentTo ?? '' })}</Label>
            <Input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} autoFocus />
            <FieldError />
          </TextField>
        )}

        <div className="entry-buttons">
          <Button type="submit" variant="primary" size="lg" fullWidth isDisabled={busy || (mustChoose && organisation === '')}>
            {busy ? say('entry.signingIn') : say('entry.signIn')}
          </Button>
          {challenge && asks === 'code' && fromAddress && (
            <Button
              variant="tertiary"
              fullWidth
              isDisabled={busy}
              onPress={() => {
                setChallenge(backToAddress(challenge))
                setCode('')
                setError(null)
              }}
            >
              {say('common.back')}
            </Button>
          )}
          {!challenge &&
            providers.map((p) => (
              <Button
                key={p.start}
                variant="secondary"
                size="lg"
                fullWidth
                isDisabled={busy}
                onPress={() => {
                  clearSignedOut()
                  window.location.assign(ssoStart(p.start, remember))
                }}
              >
                {say('entry.ssoWith', { name: p.name })}
              </Button>
            ))}
          {!challenge && windows && (
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              isDisabled={busy}
              onPress={() => {
                clearSignedOut()
                window.location.assign(windowsStart(windows, remember))
              }}
            >
              <Icon name="key" size={18} /> {say('entry.windows')}
            </Button>
          )}
        </div>
      </form>
      <Modal.Backdrop isOpen={forgot} onOpenChange={setForgot}>
        <Modal.Container size="sm">
          <Modal.Dialog>
            <Modal.CloseTrigger aria-label={say('ui.close')} />
            <Modal.Header>
              <Modal.Heading>{say('entry.forgot')}</Modal.Heading>
            </Modal.Header>
            <Modal.Body>
              <p>{say('entry.askAdmin')}</p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onPress={() => setForgot(false)}>
                {say('ui.close')}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </EntryFrame>
  )
}
